import React, { useMemo, useState, createElement } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, ArrowUpRight, ArrowDownRight, ArrowRight, Star, Calendar, Download } from 'lucide-react';
import { PageLayout } from '../../shared/PageLayout';
import { Card } from '../../../components/ui/Card';
import { DjayAvatar } from '../../../mascot';
import { MasteryRing } from '../../shared/MasteryRing';
import { MODULES, getModuleIcon } from '../../../content';
import { getAllModuleTrends, getModuleTrendDelta } from '../../../utils/activityHelpers';
import { getMasteryLabel } from '../../../utils/formatters';
import { EmptyState } from '../../../components/shared/EmptyState';
import { type LearnerAccount } from '../../../accounts';
import { AppState } from '../../../app/types';
// ============================================================
// Benchmark helper — contextualizes mastery for parent
// ============================================================
interface BenchmarkResult {
  label: string;
  emoji: string;
  colorClass: string;
  bgClass: string;
  description: string;
}
function getBenchmarkContext(mastery: number, group: number): BenchmarkResult {
  // Thresholds calibrated per group level (Dutch primary education norms)
  // Higher groups have slightly lower "on track" thresholds because content is harder
  const thresholds = {
    6: {
      excellent: 80,
      good: 60,
      average: 40
    },
    7: {
      excellent: 75,
      good: 55,
      average: 35
    },
    8: {
      excellent: 70,
      good: 50,
      average: 30
    }
  };
  const t = thresholds[group as 6 | 7 | 8] || thresholds[7];
  if (mastery >= t.excellent) {
    return {
      label: 'Uitstekend',
      emoji: '🌟',
      colorClass: 'text-emerald-700',
      bgClass: 'bg-emerald-50 border-emerald-200',
      description: `Dit is uitstekend voor groep ${group}!`
    };
  }
  if (mastery >= t.good) {
    return {
      label: 'Goed op schema',
      emoji: '👍',
      colorClass: 'text-emerald-600',
      bgClass: 'bg-emerald-50/50 border-emerald-100',
      description: `Goed op schema voor groep ${group}.`
    };
  }
  if (mastery >= t.average) {
    return {
      label: 'Gemiddeld',
      emoji: '📊',
      colorClass: 'text-amber-600',
      bgClass: 'bg-amber-50 border-amber-200',
      description: `Gemiddeld voor groep ${group}. Extra oefening helpt!`
    };
  }
  return {
    label: 'Extra oefening nodig',
    emoji: '💪',
    colorClass: 'text-rose-600',
    bgClass: 'bg-rose-50 border-rose-200',
    description: `Onder gemiddeld voor groep ${group}. Regelmatig oefenen maakt het verschil.`
  };
}
interface GrowthPageProps {
  linkedLearners: LearnerAccount[];
  selectedChild: LearnerAccount | undefined;
  childData: AppState | null;
  selectedGroup: string;
  getLearnerModuleMastery: (learnerId: string, moduleId: string) => number;
  setViewingLearner: (id: string) => void;
}
export function GrowthPage({
  linkedLearners,
  selectedChild,
  childData,
  selectedGroup,
  getLearnerModuleMastery,
  setViewingLearner
}: GrowthPageProps) {
  const [timePeriod, setTimePeriod] = useState<'week' | 'month' | 'all'>('all');
  const groupNum = parseInt(selectedGroup) || 6;
  const data = useMemo(() => {
    if (!selectedChild || !childData) return null;
    const trends = getAllModuleTrends(childData.familyMastery || {}, groupNum);
    const modules = MODULES.map(module => {
      const mastery = getLearnerModuleMastery(selectedChild.id, module.id);
      const delta = getModuleTrendDelta(childData.familyMastery || {}, groupNum, module.id);
      return {
        module,
        mastery,
        trend: trends[module.id] || 'neutral',
        delta
      };
    }).filter(m => m.mastery > 0).sort((a, b) => b.mastery - a.mastery);
    const overallMastery = modules.length > 0 ? Math.round(modules.reduce((sum, m) => sum + m.mastery, 0) / modules.length) : 0;
    const strengths = modules.filter(m => m.mastery >= 60).slice(0, 3);
    const areasForGrowth = [...modules].reverse().filter(m => m.mastery < 50).slice(0, 3);
    return {
      modules,
      overallMastery,
      strengths,
      areasForGrowth
    };
  }, [selectedChild, childData, selectedGroup, getLearnerModuleMastery, groupNum]);
  const downloadCsv = (content: string, filenamePrefix: string) => {
    const blob = new Blob([content], {
      type: 'text/csv;charset=utf-8;'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${filenamePrefix}_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const handleExportCsv = () => {
    if (!selectedChild || !data) return;
    const headers = ['Vak', 'Beheersing (%)', 'Niveau', 'Trend', 'Trend (%)'];
    const rows = data.modules.map(m => {
      const trendText = m.trend === 'up' ? 'Stijgt' : m.trend === 'down' ? 'Daalt' : 'Stabiel';
      return [`"${m.module.title}"`, m.mastery, `"${getMasteryLabel(m.mastery).label}"`, `"${trendText}"`, m.delta].join(',');
    });
    const csvContent = [headers.join(','), ...rows].join('\n');
    const safeName = selectedChild.displayName.replace(/[^a-zA-Z0-9]/g, '_');
    downloadCsv(csvContent, `groei_${safeName}`);
  };
  if (!selectedChild || !data) {
    return <PageLayout title="Groei" description="Voortgang in detail" data-id="element-4072">
        <div className="text-center py-20 text-slate-500 font-medium" data-id="element-4073">
          Geen data beschikbaar.
        </div>
      </PageLayout>;
  }
  const overallBenchmark = getBenchmarkContext(data.overallMastery, groupNum);
  return <PageLayout title="Groei" description={`Gedetailleerde voortgang van ${selectedChild.displayName}`} data-id="element-4074">
      <div className="max-w-5xl mx-auto space-y-8 pb-12" data-id="element-4075">
        {/* Child Selector */}
        {linkedLearners.length > 1 && <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide" data-id="element-4076">
            {linkedLearners.map(learner => <button key={learner.id} onClick={() => setViewingLearner(learner.id)} className={`px-5 py-2.5 rounded-xl text-sm font-bold whitespace-nowrap transition-all ${selectedChild.id === learner.id ? 'bg-emerald-600 text-white shadow-sm' : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'}`} data-id="element-4077">
                {learner.displayName}
              </button>)}
          </div>}

        {/* Time Period Filter */}
        <div className="flex items-center justify-between bg-white p-2 rounded-2xl border border-slate-200 shadow-sm" data-id="element-4078">
          <div className="flex items-center gap-2 px-3 text-slate-500" data-id="element-4079">
            <Calendar className="w-5 h-5" data-id="element-4080" />
            <span className="font-bold text-sm hidden sm:inline" data-id="element-4081">Periode:</span>
          </div>
          <div className="flex items-center gap-2" data-id="element-4082">
            <div className="flex gap-1" data-id="element-4083">
              {(['week', 'month', 'all'] as const).map(period => <button key={period} onClick={() => setTimePeriod(period)} className={`px-4 py-2 rounded-xl text-sm font-bold transition-all ${timePeriod === period ? 'bg-emerald-100 text-emerald-700 shadow-sm' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-4084">
                  {period === 'week' ? 'Deze week' : period === 'month' ? 'Deze maand' : 'Alles'}
                </button>)}
            </div>
            <div className="w-px h-6 bg-slate-200 hidden sm:block" data-id="element-4085" />
            <button onClick={handleExportCsv} className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-bold text-emerald-700 hover:bg-emerald-50 transition-all" title="Exporteer als CSV" data-id="element-4086">
              <Download className="w-4 h-4" data-id="element-4087" />
              <span className="hidden sm:inline" data-id="element-4088">CSV</span>
            </button>
          </div>
        </div>

        {/* Growth Story */}
        <div className="flex flex-col md:flex-row items-center gap-6 bg-emerald-50 p-6 rounded-3xl border border-emerald-100 shadow-sm" data-id="element-4089">
          <div className="w-24 h-24 bg-white rounded-2xl flex items-center justify-center border border-emerald-200 shadow-sm flex-shrink-0" data-id="element-4090">
            <DjayAvatar size="lg" emotion="proud" data-id="element-4091" />
          </div>
          <div className="flex-1 text-center md:text-left" data-id="element-4092">
            <h2 className="text-2xl font-black text-emerald-900 mb-2" data-id="element-4093">
              Groei-overzicht
            </h2>
            <p className="text-emerald-800 font-medium leading-relaxed" data-id="element-4094">
              {selectedChild.displayName} heeft een gemiddelde beheersing van{' '}
              <span className="font-black text-emerald-600" data-id="element-4095">
                {data.overallMastery}%
              </span>{' '}
              <span className={`font-bold ${getMasteryLabel(data.overallMastery).colorClass}`} data-id="element-4096">
                ({getMasteryLabel(data.overallMastery).label})
              </span>{' '}
              over alle geoefende vakken.
              {data.strengths.length > 0 && ` Vooral ${data.strengths[0].module.title.toLowerCase()} gaat erg goed!`}
            </p>
            {/* OH-1: Benchmark context */}
            <div className={`inline-flex items-center gap-2 mt-3 px-3 py-1.5 rounded-lg border text-sm font-bold ${overallBenchmark.bgClass} ${overallBenchmark.colorClass}`} data-id="element-4097">
              <span data-id="element-4098">{overallBenchmark.emoji}</span>
              <span data-id="element-4099">{overallBenchmark.description}</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-4100">
          {/* Strengths */}
          <section data-id="element-4101">
            <h3 className="text-lg font-black text-slate-900 flex items-center gap-2 mb-4" data-id="element-4102">
              <Star className="w-5 h-5 text-amber-500" data-id="element-4103" />
              Sterke punten
            </h3>
            <div className="space-y-3" data-id="element-4104">
              {data.strengths.map((s, i) => {
              const IconComp = getModuleIcon(s.module.icon);
              return <motion.div key={s.module.id} initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: i * 0.1
              }} data-id="element-4105">
                    <Card className="p-4 border-emerald-100 bg-white shadow-sm flex items-center gap-4" data-id="element-4106">
                      <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center flex-shrink-0" data-id="element-4107">
                        <IconComp className="w-6 h-6" data-id="element-4108" />
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-4109">
                        <h4 className="font-bold text-slate-900 truncate" data-id="element-4110">
                          {s.module.title}
                        </h4>
                        <div className="text-xs font-medium text-slate-500 truncate" data-id="element-4111">
                          {s.module.description}
                        </div>
                      </div>
                      <div className="flex-shrink-0" data-id="element-4112">
                        <MasteryRing value={s.mastery} size={48} color="emerald" data-id="element-4113" />
                      </div>
                    </Card>
                  </motion.div>;
            })}
              {data.strengths.length === 0 && <EmptyState title="Nog geen sterke punten" message="Nog niet genoeg geoefend om sterke punten te bepalen." emotion="encouraging" colorScheme="emerald" data-id="element-4114" />}
            </div>
          </section>

          {/* Areas for Growth */}
          <section data-id="element-4115">
            <h3 className="text-lg font-black text-slate-900 flex items-center gap-2 mb-4" data-id="element-4116">
              <TrendingUp className="w-5 h-5 text-indigo-500" data-id="element-4117" />
              Hier kan {selectedChild.displayName} nog groeien
            </h3>
            <div className="space-y-3" data-id="element-4118">
              {data.areasForGrowth.map((w, i) => {
              const IconComp = getModuleIcon(w.module.icon);
              return <motion.div key={w.module.id} initial={{
                opacity: 0,
                x: 20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: i * 0.1
              }} data-id="element-4119">
                    <Card className="p-4 border-indigo-100 bg-white shadow-sm flex items-center gap-4" data-id="element-4120">
                      <div className="w-12 h-12 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center flex-shrink-0" data-id="element-4121">
                        <IconComp className="w-6 h-6" data-id="element-4122" />
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-4123">
                        <h4 className="font-bold text-slate-900 truncate" data-id="element-4124">
                          {w.module.title}
                        </h4>
                        <div className="text-xs font-medium text-slate-500 truncate" data-id="element-4125">
                          Oefening baart kunst!
                        </div>
                      </div>
                      <div className="flex-shrink-0" data-id="element-4126">
                        <MasteryRing value={w.mastery} size={48} color="indigo" data-id="element-4127" />
                      </div>
                    </Card>
                  </motion.div>;
            })}
              {data.areasForGrowth.length === 0 && <EmptyState title="Geen aandachtspunten" message="Geen specifieke aandachtspunten gevonden. Goed bezig!" emotion="celebrating" colorScheme="emerald" data-id="element-4128" />}
            </div>
          </section>
        </div>

        {/* All Modules Detail */}
        <section className="pt-4" data-id="element-4129">
          <h3 className="text-lg font-black text-slate-900 mb-4" data-id="element-4130">
            Alle geoefende vakken
          </h3>
          <Card className="border-slate-200 shadow-sm bg-white overflow-hidden" data-id="element-4131">
            <div className="divide-y divide-slate-100" data-id="element-4132">
              {data.modules.map(m => {
              const IconComp = getModuleIcon(m.module.icon);
              const isGood = m.mastery >= 60;
              const isWarning = m.mastery < 40;
              const barColor = isGood ? 'bg-emerald-500' : isWarning ? 'bg-rose-500' : 'bg-amber-400';
              const moduleBenchmark = getBenchmarkContext(m.mastery, groupNum);
              return <div key={m.module.id} className="p-5 hover:bg-slate-50 transition-colors" data-id="element-4133">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6" data-id="element-4134">
                      <div className="flex items-center gap-4 sm:w-1/3" data-id="element-4135">
                        <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-500 flex-shrink-0" data-id="element-4136">
                          <IconComp className="w-5 h-5" data-id="element-4137" />
                        </div>
                        <div data-id="element-4138">
                          <div className="font-bold text-slate-900" data-id="element-4139">
                            {m.module.title}
                          </div>
                          <div className="flex items-center gap-2" data-id="element-4140">
                            <span className="text-xs font-medium text-slate-500" data-id="element-4141">
                              {getMasteryLabel(m.mastery).label}
                            </span>
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${moduleBenchmark.colorClass} ${moduleBenchmark.bgClass}`} data-id="element-4142">
                              {moduleBenchmark.label}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex-1 flex items-center gap-4" data-id="element-4143">
                        <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden" data-id="element-4144">
                          <motion.div initial={{
                        width: 0
                      }} animate={{
                        width: `${m.mastery}%`
                      }} transition={{
                        duration: 1,
                        ease: 'easeOut'
                      }} className={`h-full rounded-full ${barColor}`} data-id="element-4145" />
                        </div>
                        <div className="w-12 text-right text-xl font-black text-slate-900" data-id="element-4146">
                          {m.mastery}%
                        </div>
                        <div className="w-24 flex justify-end" data-id="element-4147">
                          {m.trend === 'up' && <span className="flex items-center gap-1 text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-md uppercase tracking-wider" data-id="element-4148">
                              <ArrowUpRight className="w-3 h-3" data-id="element-4149" />{' '}
                              {m.delta > 0 ? `+${m.delta}%` : 'Stijgt'}
                            </span>}
                          {m.trend === 'down' && <span className="flex items-center gap-1 text-xs font-bold text-rose-600 bg-rose-50 px-2 py-1 rounded-md uppercase tracking-wider" data-id="element-4150">
                              <ArrowDownRight className="w-3 h-3" data-id="element-4151" />{' '}
                              {m.delta < 0 ? `${m.delta}%` : 'Daalt'}
                            </span>}
                          {m.trend === 'stable' && <span className="flex items-center gap-1 text-xs font-bold text-slate-500 bg-slate-100 px-2 py-1 rounded-md uppercase tracking-wider" data-id="element-4152">
                              <ArrowRight className="w-3 h-3" data-id="element-4153" /> Stabiel
                            </span>}
                        </div>
                      </div>
                    </div>
                  </div>;
            })}
            </div>
          </Card>
        </section>
      </div>
    </PageLayout>;
}